import React, { useRef, useState, useEffect } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import Pagination from "../components/Pagination";
import { Avatar } from "@mui/material";
import constants from "constants";
import {
  Card,
  Container,
  Row,
  Button,
  UncontrolledTooltip
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { useNavigate } from "react-router-dom";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import DropdownFilter from "utils/DropdownFilter";
import { useAuth } from "context/AuthContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

function AdminLogTable({
  logs = [],
  isLoading,
  entityFilter,
  setEntityFilter,
  tableState,
  setTableState,
  meta,
  setShowExportModal
}) {
  const { t, language } = useTranslation();
  const { permission } = useAuth();
  const navigate = useNavigate();
  const componentRef = useRef(null);

  const [localSearch, setLocalSearch] = useState(tableState.search || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      setTableState((prev) => ({
        ...prev,
        search: localSearch,
        page: 1
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch, setTableState]);

  const handleClearFilters = () => {
    setLocalSearch("");
    setEntityFilter("");
    setTableState((prev) => ({
      ...prev,
      search: "",
      page: 1,
    }));
  };

  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === "sort") {
      const newOrder = sortOrder === "asc" ? 1 : -1;

      setTableState((prev) => {
        if (prev.sortBy === sortField && prev.sortOrder === newOrder) {
          return prev;
        }

        return {
          ...prev,
          sortBy: sortField,
          sortOrder: newOrder,
          page: 1,
        };
      });
    }

    if (type === "pagination") {
      setTableState((prev) => {
        if (prev.page === page && prev.limit === sizePerPage) {
          return prev;
        }

        return {
          ...prev,
          page,
          limit: sizePerPage,
        };
      });
    }
  };

  const columns = [
    {
      dataField: "userName",
      text: t("table.headers.userName", "User Name"),
      sort: true,
      formatter: (_, row) => {
        const user = row.user;
        if (!user) return "-";

        const picUrl = user?.profilePictureUrl
          ? `${constants.s3Bucket}/profile-pics/${user.profilePictureUrl}`
          : null;

        return (
          <div className="d-flex align-items-center">
            {picUrl ? (
              <Avatar src={picUrl} sx={{ width: 32, height: 32 }} />
            ) : (
              <Avatar
                {...helpers.stringAvatar(`${user?.firstName || ""} ${user?.lastName || ""}`)}
                sx={{ width: 32, height: 32, fontSize: 14 }}
              />
            )}
            <span className="ml-2">
              {user?.firstName} {user?.lastName}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "role",
      text: t("table.headers.role", "Role"),
      sort: true,
      formatter: (cell) => t(`table.row.${cell}`) || cell,
    },
    {
      dataField: "eventType",
      text: t("table.headers.task", "Task"),
      formatter: (cell) => t(`page.adminLogs.actions.${cell}`) || cell,
    },
    {
      dataField: "entityType",
      text: t("table.headers.entity", "Entity"),
      sort: true,
      formatter: (cell) => t(`page.adminLogs.entities.${cell}`) || cell,
    },
    {
      dataField: "createdAt",
      text: t("table.headers.date", "Journey Date"),
      sort: true,
      formatter: (cell) => (cell ? helpers.getFormattedDate(cell, t) : "-"),
    },
    {
      dataField: "actions",
      text: t("table.headers.action", "Action"),
      formatter: (_, row) => (
        <div className="d-flex align-items-center gap-1">
          <Button
            id={`view-${row._id}`}
            size="sm"
            disabled={!permission?.permissions?.modules?.adminAction?.read}
            onClick={() =>
              permission?.permissions?.modules?.adminAction?.read &&
              navigate(row.targetLink || "/")
            }
          >
            <VisibilityIcon fontSize="small" color="primary" />
          </Button>
          <UncontrolledTooltip key={`view-${row._id}-${language}`} target={`view-${row._id}`}>
            {!permission?.permissions?.modules?.adminAction?.read
              ? t("tooltip.viewNotAllowed", "View not allowed")
              : t("tooltip.viewAdminLogs", "View admin logs")}
          </UncontrolledTooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <SimpleHeader
        name={t("page.adminLogs.title", "Admin Logs")}
        parentName={t("sideNav.action", "Actions")}
      />

      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">

              <div className="py-4 position-relative" ref={componentRef}>

                <div className="d-flex flex-column flex-md-row pb-4 justify-content-between align-items-start align-items-md-center">

                  <div className="d-flex align-items-center mx-4 mb-2 mb-md-0">
                    <span className="text-sm mr-3">
                      {t("table.search", "Search")}:
                    </span>

                    <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t("table.search", "Search")}
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        style={{ paddingRight: "24px" }}
                      />
                      {localSearch && (
                        <span
                          onClick={() => {
                            setLocalSearch("");
                            setTableState((prev) => ({
                              ...prev,
                              search: "",
                              page: 1
                            }));
                          }}
                          className="position-absolute cursor-pointer search-clear-btn"
                        >
                          <CloseIcon style={{ fontSize: "14px" }} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mt-2 mt-md-0 ml-md-auto ml-4">
                    <div className="listing-header-right d-flex align-items-center">
                      <span className="text-sm font-weight-600 text-muted mr-2">
                        {t("table.filterBy", "Filter By")}
                      </span>
                      <DropdownFilter
                        value={entityFilter}
                        options={helpers.getFilter("ADMIN_LOG_ENTITIES", t)}
                        onChange={(value) => {
                          setEntityFilter(value);
                          setTableState((prev) => ({
                            ...prev,
                            page: 1
                          }));
                        }}
                        capitalize
                      />
                      <Button
                        size="sm"
                        className="mx-2 btn-neutral text-danger font-weight-600"
                        onClick={handleClearFilters}
                      >
                        {t("common.clearFilters", "Clear Filters")}
                      </Button>
                    </div>
                    <Button
                      color="primary"
                      size="md"
                      className="mt-3 mt-sm-0 ml-sm-2 mr-4"
                      onClick={() => setShowExportModal(true)}
                    >
                      {t("table.headers.export", "Export")}
                    </Button>
                  </div>
                </div>

                {/* Table */}
                <ToolkitProvider
                  keyField="_id"
                  key={language}
                  data={logs}
                  columns={columns}
                >
                  {(props) => (
                    <>
                      <div className="table-responsive max-h-[60vh] overflow-y-auto">
                        <BootstrapTable
                          {...props.baseProps}
                          bootstrap4
                          bordered={false}
                          remote={{ pagination: true, sort: true }}
                          keyField="_id"
                          onTableChange={handleTableChange}
                          defaultSorted={[
                            {
                              dataField: "createdAt",
                              order: "desc",
                            },
                          ]}
                          noDataIndication={() => (
                            <div className="text-center py-6 text-muted">
                              {isLoading ? t("common.loading", "Loading....") : t("page.adminLogs.noData", "No logs available")}
                            </div>
                          )}
                        />
                      </div>
                      <Pagination
                        totalRows={meta?.totalItems || 0}
                        rowsPerPage={tableState.limit}
                        currentPage={tableState.page - 1}
                        onPageChange={(page) => {
                          setTableState((prev) => ({
                            ...prev,
                            page: page + 1 
                          }));
                        }}
                        onRowsPerPageChange={(size) => {
                          setTableState((prev) => ({
                            ...prev,
                            limit: size,
                            page: 1
                          }));
                        }}
                        t={t}
                      />
                    </>
                  )}
                </ToolkitProvider>

              </div>

            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default AdminLogTable;
